import { z } from 'zod';
import {
  ModelProviderSchema,
  ModelProviderSecretTypeSchema,
} from './model-provider-secrets.types.js';

export const DiscoveredModelSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  providerId: z.string().min(1),
  /**
   * Hand-curated launch entries set this so the picker can default to a short
   * recommended list. Generated / pinned catalog rows omit it.
   */
  recommended: z.boolean().optional(),
});
export type DiscoveredModel = z.infer<typeof DiscoveredModelSchema>;

export const EndpointAvailabilityReasonSchema = z.enum([
  'credential_status_unknown',
  'missing_required_env_var',
  'matrix_unavailable',
]);
export type EndpointAvailabilityReason = z.infer<typeof EndpointAvailabilityReasonSchema>;

export const EndpointAvailabilitySchema = z.object({
  available: z.boolean(),
  reasons: z.array(EndpointAvailabilityReasonSchema).default([]),
  missingEnvVars: z.array(z.string()).default([]),
});
export type EndpointAvailability = z.infer<typeof EndpointAvailabilitySchema>;

export const AgentModelEndpointEnvVarSchema = z.object({
  name: z.string().min(1),
  required: z.boolean(),
  fromSecret: z.boolean(),
  description: z.string().optional(),
});
export type AgentModelEndpointEnvVar = z.infer<typeof AgentModelEndpointEnvVarSchema>;

export const AgentModelProviderSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string().optional(),
  /**
   * Secret types this provider accepts when launching this agent. Drives the
   * Launch dialog's provider-key picker:
   * - `undefined` → defaults to `['apiKey']` (back-compat)
   * - `[]` → no provider key required (free tier); the picker is hidden and
   *   launch is not gated on a key
   * - `['apiKey']` → only API keys are selectable
   * - `['apiKey', 'subscription']` → API keys + subscription keys selectable
   * Types not listed here are still shown (when fetched) but rendered disabled.
   */
  keyTypes: z.array(ModelProviderSecretTypeSchema).optional(),
  /**
   * Canonical model-provider used to filter provider-key candidates when this
   * provider's `id` is not itself a valid {@link ModelProvider} (e.g. the
   * OpenCode free-tier provider). Falls back to `id` when omitted.
   */
  modelProvider: ModelProviderSchema.optional(),
});
export type AgentModelProvider = z.infer<typeof AgentModelProviderSchema>;

/**
 * Plugin-declared endpoint configuration (without availability enrichment).
 * This is what plugins declare in their multiModel config.
 */
export const AgentModelEndpointConfigSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string().optional(),
  envVars: z.array(AgentModelEndpointEnvVarSchema),
});
export type AgentModelEndpointConfig = z.infer<typeof AgentModelEndpointConfigSchema>;

/**
 * Server/web API response shape for endpoints, enriched with availability info.
 * This extends AgentModelEndpointConfig with computed availability status.
 */
export const AgentModelEndpointSchema = AgentModelEndpointConfigSchema.extend({
  availability: EndpointAvailabilitySchema,
});
export type AgentModelEndpoint = z.infer<typeof AgentModelEndpointSchema>;

export const AgentProviderEndpointSchema = z.object({
  providerId: z.string().min(1),
  modelEndpointId: z.string().min(1),
});
export type AgentProviderEndpoint = z.infer<typeof AgentProviderEndpointSchema>;

// All agents use static catalogs: the model list is compiled in (committed
// `models.generated.ts` refreshed by the scheduled model-catalog workflow)
// and persisted globally at boot. There is no runtime discovery.
export const AgentModelDiscoverySchema = z.object({
  method: z.literal('static'),
});
export type AgentModelDiscovery = z.infer<typeof AgentModelDiscoverySchema>;

/**
 * Status of the model catalog for an agent type:
 * - 'ready': The static catalog has models
 * - 'pending': The static catalog is empty
 */
export const DiscoveryStatusSchema = z.enum(['ready', 'pending']);
export type DiscoveryStatus = z.infer<typeof DiscoveryStatusSchema>;

// ── Model Default Origin Types ──────────────────────────────────────────────
// Types for tracking how a default model was resolved for an agent type

export const ModelDefaultOriginSchema = z.enum([
  'admin',
  'service_default',
  'system_recommended',
  'fallback',
]);
export type ModelDefaultOrigin = z.infer<typeof ModelDefaultOriginSchema>;

const ResolvedModelDefaultBaseSchema = z.object({
  modelId: z.string().min(1),
  providerId: z.string().min(1),
});

export const ResolvedModelDefaultSchema = z.discriminatedUnion('origin', [
  ResolvedModelDefaultBaseSchema.extend({ origin: z.literal('admin') }),
  ResolvedModelDefaultBaseSchema.extend({ origin: z.literal('service_default') }),
  ResolvedModelDefaultBaseSchema.extend({ origin: z.literal('system_recommended') }),
  ResolvedModelDefaultBaseSchema.extend({ origin: z.literal('fallback') }),
  z.object({ origin: z.literal('unset') }),
]);
export type ResolvedModelDefault = z.infer<typeof ResolvedModelDefaultSchema>;

export const AgentModelSupportAvailableSchema = z.object({
  status: z.literal('available'),
  agentType: z.string().min(1),
  displayName: z.string().min(1),
  providers: z.array(AgentModelProviderSchema),
  endpoints: z.array(AgentModelEndpointSchema),
  providerEndpoints: z.array(AgentProviderEndpointSchema),
  defaultEndpoint: z.string().min(1),
  defaultProvider: z.string().optional(),
  defaultModel: z.string().optional(),
  defaultSource: ModelDefaultOriginSchema.optional(),
  discovery: AgentModelDiscoverySchema,
  discoveredModels: z.array(DiscoveredModelSchema).optional(),
  discoveryStatus: DiscoveryStatusSchema.optional(),
});
export type AgentModelSupportAvailable = z.infer<typeof AgentModelSupportAvailableSchema>;

export const AgentModelSupportUnavailableReasonSchema = z.enum([
  'matrix_unavailable',
  'unsupported_schema_version',
  'invalid_matrix',
]);
export type AgentModelSupportUnavailableReason = z.infer<
  typeof AgentModelSupportUnavailableReasonSchema
>;

export const AgentModelSupportUnavailableSchema = z.object({
  status: z.literal('unavailable'),
  agentType: z.string().min(1),
  reason: AgentModelSupportUnavailableReasonSchema,
  message: z.string().min(1),
});
export type AgentModelSupportUnavailable = z.infer<typeof AgentModelSupportUnavailableSchema>;

export const AgentModelSupportEntrySchema = z.discriminatedUnion('status', [
  AgentModelSupportAvailableSchema,
  AgentModelSupportUnavailableSchema,
]);
export type AgentModelSupportEntry = z.infer<typeof AgentModelSupportEntrySchema>;

// ── Launch Default Agent Type ───────────────────────────────────────────────
// Source attribution for the resolved launch-default agent type

export const LaunchDefaultAgentSourceSchema = z.enum(['admin', 'system_fallback']);
export type LaunchDefaultAgentSource = z.infer<typeof LaunchDefaultAgentSourceSchema>;

/**
 * Minimal descriptor for a launchable (registered) agent type. Appears in both
 * the public support catalog (so the Launch dialog can filter out disabled
 * types) and the admin payload (so the admin UI can render one enable/disable
 * row per registered agent, including single-model agents that do not appear
 * in the multi-model `agents` map).
 */
export const LaunchableAgentEntrySchema = z.object({
  agentType: z.string().min(1),
  displayName: z.string().min(1),
  /** Whether the plugin declares a multiModel config (admin surfaces provider/model controls for these). */
  multiModel: z.boolean(),
  /** Effective enabled state after global + org policy resolution. */
  enabled: z.boolean(),
});
export type LaunchableAgentEntry = z.infer<typeof LaunchableAgentEntrySchema>;

export const AgentModelSupportResponseSchema = z
  .object({
    agents: z.record(z.string(), AgentModelSupportEntrySchema),
    defaultAgentType: z.string().optional(),
    defaultAgentTypeSource: LaunchDefaultAgentSourceSchema.optional(),
    /**
     * Every registered agent type the server recognises, with its effective
     * enabled state. When absent (older servers), the client should treat all
     * agent types it knows about as enabled.
     */
    launchableAgents: z.array(LaunchableAgentEntrySchema).optional(),
  })
  .meta({ id: 'AgentModelSupportResponse' });
export type AgentModelSupportResponse = z.infer<typeof AgentModelSupportResponseSchema>;

// ── Launch Default Agent Request ────────────────────────────────────────────
// Request schema for setting the launch-default agent type

export const LaunchDefaultAgentRequestSchema = z.object({
  scope: z.literal('org'),
  agentType: z.string().min(1).optional(),
});
export type LaunchDefaultAgentRequest = z.infer<typeof LaunchDefaultAgentRequestSchema>;

// ── Admin Policy Types ─────────────────────────────────────────────────────

export const PolicyScopeSchema = z.literal('org');
export type PolicyScope = z.infer<typeof PolicyScopeSchema>;

export const DefaultOverrideSchema = z.object({
  modelId: z.string().min(1).optional(),
  providerId: z.string().min(1).optional(),
});
export type DefaultOverride = z.infer<typeof DefaultOverrideSchema>;

export const AgentModelPolicySchema = z.object({
  providers: z.record(z.string(), z.boolean()),
  models: z.record(z.string(), z.boolean()),
  default: DefaultOverrideSchema,
});
export type AgentModelPolicy = z.infer<typeof AgentModelPolicySchema>;

// ── Admin Agent Models Response ─────────────────────────────────────────────
// Extended response that includes unfiltered catalog data for admin UI

export const AdminAgentModelEntrySchema = z.object({
  agentType: z.string(),
  displayName: z.string(),
  /** All providers from the plugin config (unfiltered by policy) */
  providers: z.array(AgentModelProviderSchema),
  /** All endpoints from the plugin config (unfiltered by policy) */
  endpoints: z.array(AgentModelEndpointSchema),
  /** All discovered models (unfiltered by policy) */
  discoveredModels: z.array(DiscoveredModelSchema).optional(),
  discoveryStatus: DiscoveryStatusSchema.optional(),
  /** Organization policy state. */
  effectivePolicy: AgentModelPolicySchema,
  /** Effective default model/provider with origin attribution */
  effectiveDefault: ResolvedModelDefaultSchema.optional(),
});
export type AdminAgentModelEntry = z.infer<typeof AdminAgentModelEntrySchema>;

/**
 * Admin-facing availability row for a registered agent type. Covers both
 * multi-model and single-model agents.
 */
export const AdminLaunchableAgentEntrySchema = z.object({
  agentType: z.string().min(1),
  displayName: z.string().min(1),
  multiModel: z.boolean(),
  /** Org-level explicit enable/disable. `undefined` means "inherit default (enabled)". */
  orgEnabled: z.boolean().optional(),
  /** Resolved effective value used for launch filtering. */
  effectiveEnabled: z.boolean(),
});
export type AdminLaunchableAgentEntry = z.infer<typeof AdminLaunchableAgentEntrySchema>;

export const AdminAgentModelsResponseSchema = z.object({
  agents: z.array(AdminAgentModelEntrySchema),
  /** Raw org-level launch default agent type (undefined if not configured) */
  orgLaunchDefaultAgentType: z.string().optional(),
  /** Effective launch default agent type after resolution (undefined if all agents disabled) */
  effectiveLaunchDefaultAgentType: z.string().optional(),
  /** Source of the effective launch default */
  effectiveLaunchDefaultAgentTypeSource: LaunchDefaultAgentSourceSchema,
  /**
   * Every registered agent type (multi-model and single-model) with its
   * agent-level enable/disable state. Used by the admin UI to render the
   * top-level availability toggle introduced in PRD-24.
   */
  launchableAgents: z.array(AdminLaunchableAgentEntrySchema).optional(),
});
export type AdminAgentModelsResponse = z.infer<typeof AdminAgentModelsResponseSchema>;

export const PolicyToggleRequestSchema = z.object({
  enabled: z.boolean(),
  scope: PolicyScopeSchema,
});
export type PolicyToggleRequest = z.infer<typeof PolicyToggleRequestSchema>;

export const DefaultOverrideRequestSchema = z
  .object({
    modelId: z
      .string()
      .nullish()
      .transform((v) => (v === '' ? undefined : v)),
    providerId: z
      .string()
      .nullish()
      .transform((v) => (v === '' ? undefined : v)),
    scope: PolicyScopeSchema,
  })
  .superRefine((value, ctx) => {
    const hasModel = value.modelId != null;
    const hasProvider = value.providerId != null;
    if (hasModel !== hasProvider) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'modelId and providerId must be provided together or cleared together',
        path: hasModel ? ['providerId'] : ['modelId'],
      });
    }
  });
export type DefaultOverrideRequest = z.infer<typeof DefaultOverrideRequestSchema>;
